import { z } from 'zod';
import { api } from '@/lib/api';
import {
  ConnectorListResponseSchema,
  ConnectorSchema,
  ConnectorGrantsResponseSchema,
  MemberConnectorGrantsResponseSchema,
  BeginOAuthResponseSchema,
  MyGrantsResponseSchema,
  InvokeResponseSchema,
} from './schemas';

function headers(tenantId: string) {
  return { 'X-Tenant-Id': tenantId };
}

export async function listConnectors(tenantId: string) {
  return api.get('/tenant/connectors', ConnectorListResponseSchema, { headers: headers(tenantId) });
}

export async function saveApiKey(tenantId: string, connectorId: string, apiKey: string) {
  return api.post(
    `/tenant/connectors/${encodeURIComponent(connectorId)}/api-key`,
    ConnectorSchema,
    { apiKey },
    { headers: headers(tenantId) },
  );
}

export async function beginOAuth(
  tenantId: string,
  connectorId: string,
  clientId: string,
  clientSecret: string,
) {
  return api.post(
    `/tenant/connectors/${encodeURIComponent(connectorId)}/oauth/begin`,
    BeginOAuthResponseSchema,
    { clientId, clientSecret },
    { headers: headers(tenantId) },
  );
}

export async function disconnectConnector(tenantId: string, connectorId: string) {
  return api.delete(
    `/tenant/connectors/${encodeURIComponent(connectorId)}`,
    z.object({ disconnected: z.literal(true) }),
    { headers: headers(tenantId) },
  );
}

export async function listGrants(tenantId: string, connectorId: string) {
  return api.get(
    `/tenant/connectors/${encodeURIComponent(connectorId)}/grants`,
    ConnectorGrantsResponseSchema,
    { headers: headers(tenantId) },
  );
}

export async function createGrant(
  tenantId: string,
  connectorId: string,
  memberId: string,
  operationAllowlist: string[] | null = null,
) {
  return api.post(
    `/tenant/connectors/${encodeURIComponent(connectorId)}/grants`,
    z.object({ granted: z.literal(true) }),
    { memberId, operationAllowlist },
    { headers: headers(tenantId) },
  );
}

export async function patchGrant(
  tenantId: string,
  connectorId: string,
  memberId: string,
  operationAllowlist: string[] | null,
) {
  return api.patch(
    `/tenant/connectors/${encodeURIComponent(connectorId)}/grants/${encodeURIComponent(memberId)}`,
    z.object({ updated: z.literal(true) }),
    { operationAllowlist },
    { headers: headers(tenantId) },
  );
}

export async function revokeGrant(tenantId: string, connectorId: string, memberId: string) {
  return api.delete(
    `/tenant/connectors/${encodeURIComponent(connectorId)}/grants/${encodeURIComponent(memberId)}`,
    z.object({ revoked: z.literal(true) }),
    { headers: headers(tenantId) },
  );
}

export async function getMemberConnectorGrants(tenantId: string, memberId: string) {
  return api.get(
    `/tenant/members/${encodeURIComponent(memberId)}/connector-grants`,
    MemberConnectorGrantsResponseSchema,
    { headers: headers(tenantId) },
  );
}

export async function getMyGrants(tenantId: string) {
  return api.get('/tenant/connectors/my-grants', MyGrantsResponseSchema, {
    headers: headers(tenantId),
  });
}

export async function invokeConnector(
  tenantId: string,
  connectorId: string,
  operationName: string,
  input: Record<string, unknown>,
) {
  return api.post(
    `/tenant/connectors/${encodeURIComponent(connectorId)}/invoke`,
    InvokeResponseSchema,
    { operationName, input },
    { headers: headers(tenantId) },
  );
}
